import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Database } from '../lib/database.types';
import type { CartItem } from '../lib/database.types';
import VenueChangingModal from '../components/VenueChangingModal';
import { supabase } from '../lib/supabase';
import { AppliedCoupon } from '../types/coupon';
import { validateCoupon, validationResultToAppliedCoupon } from '../lib/couponApi';
import { ZAP_FEE_RATE } from '@zap/shared-logic';

type MenuItem = Database['public']['Tables']['menu_items']['Row'];
type Venue = Database['public']['Tables']['venues']['Row'];

interface CartContextType {
  items: CartItem[];
  addItem: (item: MenuItem, quantity?: number, customizations?: Record<string, string>) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  total: number;
  subtotal: number;
  taxAmount: number;
  zapFee: number;
  gratuityAmount: number;
  tipAmount: number;
  tipPercentage: number | null;
  setTipPercentage: (percentage: number | null) => void;
  setCustomTipAmount: (amount: number) => void;
  itemCount: number;
  currentVenueId: string | null;
  currentVenueName: string | null;
  venueGratuityEnabled: boolean;
  venueGratuityRate: number;
  venueSettingsLoaded: boolean;
  venueCurrency: string;
  appliedCoupon: AppliedCoupon | null;
  discountAmount: number;
  applyCoupon: (code: string, userId: string) => Promise<{ success: boolean; error?: string }>;
  removeCoupon: () => void;
  isCouponValidating: boolean;
  // Table context
  selectedTableId: string | null;
  selectedTableName: string | null;
  isTableOrder: boolean;
  // Bottle service context
  isBottleService: boolean;
  bottleServiceStaffId: string | null;
  bottleServiceStaffName: string | null;
  setTableSelection: (tableId: string | null, tableName: string | null) => void;
  setBottleService: (enabled: boolean, staffId?: string | null, staffName?: string | null) => void;
  clearOrderContext: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    const stored = localStorage.getItem('cart');
    return stored ? JSON.parse(stored) : [];
  });
  const [tipPercentage, setTipPercentage] = useState<number | null>(null);
  const [customTipAmount, setCustomTipAmount] = useState<number>(0);
  const [currentVenueId, setCurrentVenueId] = useState<string | null>(() => {
    // Initialize venue ID from cart items if available
    const stored = localStorage.getItem('cart');
    if (stored) {
      const cartItems = JSON.parse(stored) as CartItem[];
      return cartItems.length > 0 ? cartItems[0].venue_id || null : null;
    }
    return null;
  });
  const [currentVenueName, setCurrentVenueName] = useState<string | null>(null);
  const [venueGratuityEnabled, setVenueGratuityEnabled] = useState<boolean>(false);
  const [venueGratuityRate, setVenueGratuityRate] = useState<number>(0.18);
  // Whether the venue's gratuity/currency settings have finished loading. Checkout must
  // wait for this before creating the order, otherwise an order can be saved with
  // gratuity_amount=0 while the charge (computed after settings load) includes gratuity.
  const [venueSettingsLoaded, setVenueSettingsLoaded] = useState<boolean>(false);
  const [venueCurrency, setVenueCurrency] = useState<string>('USD');
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [isCouponValidating, setIsCouponValidating] = useState<boolean>(false);

  // Table context state
  const [selectedTableId, setSelectedTableId] = useState<string | null>(() => {
    const stored = localStorage.getItem('cart_table_context');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.selectedTableId ?? null;
    }
    return null;
  });
  const [selectedTableName, setSelectedTableName] = useState<string | null>(() => {
    const stored = localStorage.getItem('cart_table_context');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.selectedTableName ?? null;
    }
    return null;
  });
  const [isTableOrder, setIsTableOrder] = useState<boolean>(() => {
    const stored = localStorage.getItem('cart_table_context');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.isTableOrder ?? false;
    }
    return false;
  });

  // Bottle service context state
  const [isBottleService, setIsBottleService] = useState<boolean>(() => {
    const stored = localStorage.getItem('cart_bottle_service');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.isBottleService ?? false;
    }
    return false;
  });
  const [bottleServiceStaffId, setBottleServiceStaffId] = useState<string | null>(() => {
    const stored = localStorage.getItem('cart_bottle_service');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.bottleServiceStaffId ?? null;
    }
    return null;
  });
  const [bottleServiceStaffName, setBottleServiceStaffName] = useState<string | null>(() => {
    const stored = localStorage.getItem('cart_bottle_service');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.bottleServiceStaffName ?? null;
    }
    return null;
  });

  // Modal state for venue changing
  const [showVenueChangeModal, setShowVenueChangeModal] = useState(false);
  const [pendingItem, setPendingItem] = useState<{
    item: MenuItem;
    quantity: number;
    customizations?: Record<string, string>;
    venueName: string;
  } | null>(null);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  // Persist table context to localStorage
  useEffect(() => {
    localStorage.setItem('cart_table_context', JSON.stringify({
      selectedTableId,
      selectedTableName,
      isTableOrder,
    }));
  }, [selectedTableId, selectedTableName, isTableOrder]);

  // Persist bottle service context to localStorage
  useEffect(() => {
    localStorage.setItem('cart_bottle_service', JSON.stringify({
      isBottleService,
      bottleServiceStaffId,
      bottleServiceStaffName,
    }));
  }, [isBottleService, bottleServiceStaffId, bottleServiceStaffName]);

  // Fetch venue gratuity settings when venue changes
  useEffect(() => {
    const fetchVenueSettings = async () => {
      if (!currentVenueId) {
        setVenueGratuityEnabled(false);
        setVenueGratuityRate(0.18);
        setVenueCurrency('USD');
        setVenueSettingsLoaded(true);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('venues')
          .select('is_gratuity_enabled, gratuity_rate, currency')
          .eq('id', currentVenueId)
          .single();

        if (error) {
          console.error('Error fetching venue settings:', error);
          return;
        }

        if (data) {
          setVenueGratuityEnabled(data.is_gratuity_enabled || false);
          setVenueGratuityRate(data.gratuity_rate || 0.18);
          setVenueCurrency(data.currency || 'USD');
        }
      } catch (error) {
        console.error('Error fetching venue settings:', error);
      } finally {
        // Settings resolved (success or failure) — safe for checkout to build the order.
        setVenueSettingsLoaded(true);
      }
    };

    // Re-gate while (re)loading for a new venue.
    setVenueSettingsLoaded(false);
    fetchVenueSettings();
  }, [currentVenueId]);

  // Calculate the additional price for customizations
  const calculateCustomizationPrice = (
    item: MenuItem, 
    customizations?: Record<string, string>
  ): number => {
    if (!item.customization_options || !Array.isArray(item.customization_options) || !customizations) {
      return 0;
    }
    
    let additionalPrice = 0;
    
    for (const option of item.customization_options) {
      const selectedOption = customizations[option.name];
      if (selectedOption && option.options) {
        const optionDetails = option.options.find((opt: any) => opt.name === selectedOption);
        if (optionDetails && optionDetails.price) {
          additionalPrice += Number(optionDetails.price);
        }
      }
    }
    
    return additionalPrice;
  };

  const addItem = (
    item: MenuItem,
    quantity = 1,
    customizations?: Record<string, string>
  ) => {
    // Check if we need to validate venue
    if (currentVenueId === null) {
      // First item being added, set the venue
      setCurrentVenueId(item.venue_id || null);
      setCurrentVenueName(item.venue_name || 'this venue');
      
      // Proceed with adding the item
      addItemToCart(item, quantity, customizations);
    } else if (item.venue_id !== currentVenueId) {
      // Item is from a different venue, show the modal
      setPendingItem({
        item,
        quantity,
        customizations,
        venueName: item.venue_name || 'new venue'
      });
      setShowVenueChangeModal(true);
    } else {
      // Item is from the same venue, add it to cart
      addItemToCart(item, quantity, customizations);
    }
  };

  const addItemToCart = (
    item: MenuItem,
    quantity = 1,
    customizations?: Record<string, string>
  ) => {
    setItems(currentItems => {
      // Find existing item with same customizations
      const existingItem = currentItems.find(i => 
        i.id === item.id && 
        JSON.stringify(i.customizations) === JSON.stringify(customizations)
      );
      
      // Calculate total item price including customizations
      const customizationPrice = calculateCustomizationPrice(item, customizations);
      const totalItemPrice = item.price + customizationPrice;
      
      if (existingItem) {
        return currentItems.map(i =>
          i.id === item.id && 
          JSON.stringify(i.customizations) === JSON.stringify(customizations)
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }

      return [...currentItems, { 
        ...item, 
        quantity, 
        customizations,
        price: totalItemPrice, // Update price to include customizations
        basePrice: item.price, // Store original price for reference
        customizationPrice // Store customization price for reference
      }];
    });
  };

  const handleKeepCurrentVenue = () => {
    setShowVenueChangeModal(false);
    setPendingItem(null);
  };

  const handleSwitchVenue = () => {
    if (pendingItem) {
      // Clear the cart (this also clears order context)
      clearCart();

      // Set the new venue
      setCurrentVenueId(pendingItem.item.venue_id || null);
      setCurrentVenueName(pendingItem.venueName);

      // Explicitly clear order context for venue change
      setSelectedTableId(null);
      setSelectedTableName(null);
      setIsTableOrder(false);
      setIsBottleService(false);
      setBottleServiceStaffId(null);
      setBottleServiceStaffName(null);
      
      // Add the new item
      addItemToCart(
        pendingItem.item,
        pendingItem.quantity,
        pendingItem.customizations
      );
      
      // Show success toast for the newly added item
      import('react-hot-toast').then(toast => {
        toast.default.success(`Added ${pendingItem.quantity} ${pendingItem.item.name} to cart`, {
          icon: '🛍️',
          style: {
            background: '#27272a',
            color: '#fff',
            border: '1px solid #3f3f46',
          },
        });
      });
      
      // Close the modal
      setShowVenueChangeModal(false);
      setPendingItem(null);
    }
  };

  const removeItem = (itemId: string) => {
    setItems(currentItems => {
      const filteredItems = currentItems.filter(item => item.id !== itemId);
      
      // If we removed all items, reset the current venue
      if (filteredItems.length === 0) {
        setCurrentVenueId(null);
        setCurrentVenueName(null);
      }
      
      return filteredItems;
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(itemId);
      return;
    }

    setItems(currentItems =>
      currentItems.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = useCallback(() => {
    setItems([]);
    setTipPercentage(null);
    setCustomTipAmount(0);
    setCurrentVenueId(null);
    setCurrentVenueName(null);
    setVenueGratuityEnabled(false);
    setVenueGratuityRate(0.18);
    setVenueCurrency('USD');
    setAppliedCoupon(null);
    // Clear order context (table and bottle service selections)
    setSelectedTableId(null);
    setSelectedTableName(null);
    setIsTableOrder(false);
    setIsBottleService(false);
    setBottleServiceStaffId(null);
    setBottleServiceStaffName(null);
  }, []);

  // Apply coupon to cart
  const applyCoupon = async (code: string, userId: string): Promise<{ success: boolean; error?: string }> => {
    if (!currentVenueId) {
      return { success: false, error: 'Please add items to your cart first' };
    }

    if (items.length === 0) {
      return { success: false, error: 'Your cart is empty' };
    }

    setIsCouponValidating(true);

    try {
      const result = await validateCoupon(code, userId, currentVenueId, subtotal);

      if (!result.valid) {
        setIsCouponValidating(false);
        return { success: false, error: result.error || 'Invalid coupon code' };
      }

      const appliedCouponData = validationResultToAppliedCoupon(result);

      if (!appliedCouponData) {
        setIsCouponValidating(false);
        return { success: false, error: 'Failed to apply coupon' };
      }

      setAppliedCoupon(appliedCouponData);
      setIsCouponValidating(false);
      return { success: true };
    } catch (error) {
      console.error('Error applying coupon:', error);
      setIsCouponValidating(false);
      return { success: false, error: 'An error occurred while applying the coupon' };
    }
  };

  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
  };

  // Clear order context (table and bottle service selections)
  const clearOrderContext = useCallback(() => {
    setSelectedTableId(null);
    setSelectedTableName(null);
    setIsTableOrder(false);
    setIsBottleService(false);
    setBottleServiceStaffId(null);
    setBottleServiceStaffName(null);
  }, []);

  // Set table selection
  const setTableSelection = useCallback((tableId: string | null, tableName: string | null) => {
    setSelectedTableId(tableId);
    setSelectedTableName(tableName);
    setIsTableOrder(tableId !== null);
  }, []);

  // Set bottle service
  const setBottleService = useCallback((enabled: boolean, staffId?: string | null, staffName?: string | null) => {
    setIsBottleService(enabled);
    setBottleServiceStaffId(staffId ?? null);
    setBottleServiceStaffName(staffName ?? null);
  }, []);

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Calculate discount amount from applied coupon
  const discountAmount = appliedCoupon?.discount_amount || 0;

  // Adjusted subtotal after discount (can't be negative)
  const adjustedSubtotal = Math.max(0, subtotal - discountAmount);

  // Calculate tax as 8% of adjusted subtotal (food and beverages only)
  const taxAmount = Math.round(adjustedSubtotal * 0.08 * 100) / 100;

  // Calculate gratuity amount if enabled for the venue (based on adjusted subtotal)
  const gratuityAmount = venueGratuityEnabled
    ? Math.round(adjustedSubtotal * venueGratuityRate * 100) / 100
    : 0;

  // Calculate tip amount based on percentage or custom amount (on original subtotal)
  const tipAmount = tipPercentage ? Math.round(subtotal * tipPercentage) / 100 : customTipAmount;

  // Calculate the Zap fee (rate from @zap/shared-logic — single source of truth). For
  // VENUE-funded discounts the fee is charged on the FULL (pre-discount) subtotal so the venue
  // absorbs the promo and Zap stays whole. For Zap-funded (or no) discounts the fee is on the
  // adjusted subtotal (Zap covers its share; the payment-intent function further reduces the
  // platform fee to make the venue whole).
  const zapFeeBase = appliedCoupon?.funded_by === 'venue' ? subtotal : adjustedSubtotal;
  const zapFee = Math.round(zapFeeBase * ZAP_FEE_RATE * 100) / 100;

  // Total includes adjusted subtotal, tax, gratuity, Zap fee, and tip
  const total = adjustedSubtotal + taxAmount + gratuityAmount + zapFee + tipAmount;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        total,
        subtotal,
        taxAmount,
        zapFee,
        gratuityAmount,
        tipAmount,
        tipPercentage,
        setTipPercentage,
        setCustomTipAmount,
        itemCount,
        currentVenueId,
        currentVenueName,
        venueGratuityEnabled,
        venueGratuityRate,
        venueSettingsLoaded,
        venueCurrency,
        appliedCoupon,
        discountAmount,
        applyCoupon,
        removeCoupon,
        isCouponValidating,
        // Table context
        selectedTableId,
        selectedTableName,
        isTableOrder,
        // Bottle service context
        isBottleService,
        bottleServiceStaffId,
        bottleServiceStaffName,
        setTableSelection,
        setBottleService,
        clearOrderContext,
      }}
    >
      {children}
      
      {/* Venue Changing Modal */}
      {showVenueChangeModal && pendingItem && (
        <VenueChangingModal
          isOpen={showVenueChangeModal}
          onClose={handleKeepCurrentVenue}
          currentVenueName={currentVenueName}
          newVenueName={pendingItem.venueName}
          onKeepCurrent={handleKeepCurrentVenue}
          onSwitchVenue={handleSwitchVenue}
        />
      )}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}